import { Badge, Button, Spinner, useToast } from "@design";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { ApiError, deleteTeamMutation, errorMessage, getTeamOptions, type TeamResponse } from "@/api";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Text } from "@/components/Text";
import { NotFoundPage } from "@/features/errors/NotFoundPage";
import { useViewport } from "@/responsive/viewport";
import { TeamFormModal } from "./TeamFormModal";
import { TeamMembersSection } from "./TeamMembersSection";
import { useTeamAccess } from "./teamRoles";
import { useRefreshTeams } from "./useRefreshTeams";

/** Where the page was opened from: Admin > Teams (platform admins) or Settings > Teams (anyone in the team). */
type From = "admin" | "settings";

function TeamHeader({ team }: Readonly<{ team: TeamResponse }>) {
  const { t } = useTranslation("teams");
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Text variant="h2" as="h1">
          {team.name}
        </Text>
        {team.isEveryone && <Badge variant="info">{t("everyone")}</Badge>}
      </div>
      {team.description && <Text tone="secondary">{team.description}</Text>}
      <Text variant="body-sm" tone="tertiary">
        {t("memberCount", { count: team.members?.length ?? 0 })}
      </Text>
    </div>
  );
}

function TeamActions({ team, listPath }: Readonly<{ team: TeamResponse; listPath: string }>) {
  const { t } = useTranslation("teams");
  const toast = useToast();
  const navigate = useNavigate();
  const refreshTeams = useRefreshTeams();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const del = useMutation({
    ...deleteTeamMutation(),
    onSuccess: async () => {
      toast.success(t("toast.deleted", { name: team.name }));
      setDeleting(false);
      await refreshTeams();
      navigate(listPath);
    },
    onError: (error) => toast.error(errorMessage(error, t("confirmDelete.failed"))),
  });
  return (
    <>
      <Button variant="secondary" size="sm" leadingIcon={<Pencil size={16} />} onClick={() => setEditing(true)}>
        {t("detail.edit")}
      </Button>
      {!team.isEveryone && (
        <Button variant="ghost" size="sm" leadingIcon={<Trash2 size={16} />} onClick={() => setDeleting(true)}>
          {t("detail.delete")}
        </Button>
      )}
      {editing && <TeamFormModal team={team} onClose={() => setEditing(false)} />}
      <ConfirmDialog
        open={deleting}
        title={t("confirmDelete.title", { name: team.name })}
        body={t("confirmDelete.body")}
        confirmLabel={t("confirmDelete.confirm")}
        loading={del.isPending}
        onConfirm={() => del.mutate({ path: { team_uuid: team.uuid }, throwOnError: true })}
        onClose={() => setDeleting(false)}
      />
    </>
  );
}

/**
 * One team: who's in it and their roles (MHS-1082). Platform admins rename, describe and delete it; they and the
 * team's admins manage members. Admin is read-only on phones, as elsewhere; Settings isn't.
 */
export function TeamPage({ from }: Readonly<{ from: From }>) {
  const { t } = useTranslation(["teams", "settings"]);
  const areas = useAreaCrumbs();
  const { isMobile } = useViewport();
  const { teamUuid = "" } = useParams();
  const query = useQuery({ ...getTeamOptions({ path: { team_uuid: teamUuid } }), select: (r) => r.data });
  const team = query.data;
  const access = useTeamAccess(team);
  const listPath = from === "admin" ? "/admin/teams" : "/settings/teams";
  const onDevice = from === "settings" || !isMobile;

  if (query.error instanceof ApiError && (query.error.status === 404 || query.error.status === 403)) {
    return <NotFoundPage />;
  }
  const trail =
    from === "admin"
      ? [areas.admin, { label: t("teams:title"), to: listPath }]
      : [areas.settings, { label: t("settings:teams.title"), to: listPath }];

  let readOnlyNote: string | undefined;
  if (!access.canManageMembers) readOnlyNote = t("teams:detail.readOnly");
  else if (!onDevice) readOnlyNote = t("teams:detail.readOnlyPhone");

  return (
    <div className="flex flex-col">
      <PageBreadcrumbs
        trail={trail}
        current={team?.name ?? ""}
        actions={team && access.canEditTeam && onDevice ? <TeamActions team={team} listPath={listPath} /> : undefined}
      />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4 py-6 md:px-8">
        {query.isPending && <Spinner />}
        {query.isError && !query.isPending && (
          <Text tone="error" role="alert">
            {t("teams:detail.loadFailed")}
          </Text>
        )}
        {team && (
          <>
            <TeamHeader team={team} />
            <TeamMembersSection
              team={team}
              canManage={access.canManageMembers && onDevice}
              readOnlyNote={readOnlyNote}
              myUuid={access.myUuid}
            />
          </>
        )}
      </div>
    </div>
  );
}
